import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { applicationDownQuery, applicationsDashboardQuery, deviceDownQuery, type DownEntry } from '../api/proactivitee'
import { BoardCard, BoardGrid, BoardLine, BoardList, BoardLook, BoardStat, BoardStrip } from '../dashboard/BoardLook'
import { toneOfPanel, worstTone } from '../dashboard/boardTone'
import { Content } from '../ui/Shell'

/**
 * Applications Dashboard, pe desenul tablourilor de bord.
 * =============================================================================
 *
 * ACELEASI CIFRE, ALTA HAINA. Ecranul arata mai departe fix ce arata si
 * inainte: contoarele zonelor alese de client (pana la sase), lista
 * aplicatiilor cu cate masini are fiecare, listele de masini ale celor patru
 * categorii si ale dezvoltarii, culoarea fiecarui rand dupa cea mai rea stare a
 * lui, si sertarul care spune ce anume e cazut - cu persoana de contact la
 * aplicatii. Nimic nu se citeste altfel si nimic nu se citeste din alta parte:
 * acelasi raspuns, `GET /api/v2/proactivitee/applications-dashboard`, si
 * aceleasi doua capete de sertar.
 *
 * Ce se schimba e desenul. Dalele colorate de la Metronic (`dashboard-stat`) si
 * panourile `panel panel-dashboard` cu chenar rosu/galben/verde lasa locul
 * cardului tablourilor: alb, colturi de 14px, umbra in loc de chenar, cifra de
 * 40px, placuta colorata a modulului si culorile de stare din `tokens.css`.
 * Acelasi limbaj ca `/app/proactivitee/core_applications_dashboard`, mutat cu o
 * ora inainte, si ca celelalte tablouri.
 *
 * -----------------------------------------------------------------------------
 * SASE COLOANE DEVIN SASE CARDURI
 * -----------------------------------------------------------------------------
 *
 * Vechiul ecran avea doua etaje: un rand de dale cu cate sunt cazute pe fiecare
 * zona, si sub el pana la sase coloane Bootstrap, fiecare un teanc de panouri -
 * unul pe aplicatie sau pe masina. Coloanele n-aveau titlu: se ghicea din
 * ordinea dalelor de deasupra care coloana e care.
 *
 * Acum:
 *
 *   dala cu "cate sunt cazute"  -> contorul din banda de sus (`BoardStrip`),
 *                                  neschimbat: cifra sau "100%", Down / Up, si
 *                                  eticheta zonei;
 *   coloana de panouri          -> un CARD, cu numele zonei scris pe el si cu
 *                                  lista inauntru.
 *
 * Cardul poarta si ce n-avea coloana: numele zonei, cate randuri are, si cate
 * merg / cate nu, in subsol. Nimic din ce era nu pleaca.
 *
 * -----------------------------------------------------------------------------
 * DE CE NU INTRA IN CATALOG (si de ce asta NU e o conversie)
 * -----------------------------------------------------------------------------
 *
 * Tablourile noi isi iau cardurile dintr-un CATALOG scris de mana
 * (`application/Services/Dashboard/Catalog.php`): fiecare card e o cifra cu o
 * tinta, doua praguri, un drept si o linie de istorie pe zile. Ecranul asta nu
 * incape acolo, din aceleasi trei motive pentru care nu incape nici vecinul lui:
 * listele sunt ALE CLIENTULUI si cresc cand omul mai adauga o aplicatie sau mai
 * pune o masina la urmarit, cifrele n-au istorie pe zile, si dreptul se cere pe
 * ECRAN (`proactivitee.applications_dashboard.read`), o data.
 *
 * Varianta cealalta - un fel NOU de card in catalog, "cate unul pentru fiecare
 * rand al citirii" - a fost cantarita si lasata pe 11 septembrie 2026: ar fi
 * insemnat umblat in `Catalog`, `Card`, `Board`, `Boards`, `Front`, `CardMarkup`
 * si in paginarea wallboard-ului, noua mii de randuri pe care stau cinci
 * tablouri si peretele din NOC. Pe 10 septembrie un singur element in plus pe
 * calea aia a asezat cardurile unul peste altul pe toate cinci deodata. Nu se
 * redeschide.
 *
 * Deci: se ia DESENUL, se lasa MASINARIA. Piesele sunt cele comune din
 * `dashboard/BoardLook.tsx`, scrise chiar pentru ecranele care se muta asa - nu
 * se scrie aici a patra copie a aceluiasi ajutor.
 *
 * NU intra in contradictie cu regula lui Bogdan din 6 septembrie 2026 -
 * "tablourile AmCharts clasice nu se porteaza" (`docs/decizie-v4-rescriere.md`,
 * si `DROPPED` din `tools/v4_inventory.py`). Alea sunt `Proactivitee::dashboard`
 * si surorile lui, ecrane care raman in CodeIgniter si nu se mai ating. Asta e
 * `Proactivitee::applications_dashboard`, care sta in `MOVED` si ruleaza deja pe
 * /app: nu se converteste nimic, un ecran care e DEJA in v4 isi schimba haina.
 *
 * -----------------------------------------------------------------------------
 * DOUA LUCRURI STRICATE, GASITE LA MUTARE
 * -----------------------------------------------------------------------------
 *
 * 1. ALERTA "no dashboard elements" SE ARATA LA FIECARE INCARCARE. Lista de
 *    zone se citea din raspuns cu `?? {}`, iar cat timp raspunsul nu venise
 *    lista era goala - adica exact semnul dupa care ecranul hotara ca omul n-a
 *    ales nimic. La fiecare intrare pe ecran clipea "There are no dashboard
 *    elements selected!" inainte sa se deseneze tabloul. Acum se intreaba daca
 *    raspunsul A VENIT, nu daca e gol.
 *
 * 2. ECRANUL NU SE MAI IMPROSPATA SINGUR. In portal, `index-1.js` reincarca
 *    toate cele patru bucati la fiecare doua minute (`setInterval(..., 120000)`)
 *    - e un ecran de supraveghere, lasat deschis pe un monitor, si o masina
 *    cazuta acum zece minute trebuie sa se vada. La mutarea in v4 intervalul s-a
 *    pierdut: pagina arata starea din clipa in care a fost deschisa, pana o
 *    reincarca cineva cu F5. S-a pus inapoi, pe citire
 *    (`applicationsDashboardQuery`), cu aceleasi doua minute.
 */

type App = { app_id: number; app_name: string; devices: number; panel: string }
type Device = { device_id: number; device_name: string; panel: string }

/**
 * Cele sase zone, in ordinea din portal.
 *
 * `key` e numele cu care clientul si-a bifat zona in Settings - inclusiv
 * `core_aplications`, scris cu un singur "p" in baza din ziua intai; `counter`
 * e cheia din raspuns; `title` si `icon` sunt exact eticheta si iconita dalei
 * vechi din `dashboard.php` ("Servers", nu "Physical Servers": asa scria acolo).
 */
const GROUPS = [
  { key: 'core_aplications', counter: 'core_apps', title: 'Core Applications', icon: 'fa-database', apps: true },
  { key: 'virtual_infrastructure', counter: 'virtual_infrastructure', title: 'Virtual Infrastructure', icon: 'fa-tasks', apps: false },
  { key: 'physical_servers', counter: 'physical_servers', title: 'Servers', icon: 'fa-server', apps: false },
  { key: 'data_storage', counter: 'data_storage', title: 'Data Storage', icon: 'fa-hdd', apps: false },
  { key: 'network_infrastructure', counter: 'network_infrastructure', title: 'Network Infrastructure', icon: 'fa-sitemap', apps: false },
  { key: 'development_infrastructure', counter: 'development_infrastructure', title: 'Development Infrastructure', icon: 'fa-sitemap', apps: false },
] as const

export function ApplicationsDashboardPage() {
  const query = useQuery(applicationsDashboardQuery())
  /* Aplicatiile: un singur sertar deschis (`data-parent="#applications"` in
     markupul vechi). Masinile n-aveau parinte comun in portal, deci acolo se
     pot deschide mai multe deodata. */
  const [openApp, setOpenApp] = useState<number | null>(null)
  const [openDevices, setOpenDevices] = useState<number[]>([])

  const elements = (query.data?.elements ?? {}) as Record<string, number>
  const counters = (query.data?.counters ?? {}) as Record<string, number>
  const applications = (query.data?.applications ?? []) as App[]
  const groups = (query.data?.groups ?? {}) as Record<string, Device[]>
  const shown = GROUPS.filter((group) => group.key in elements)

  /*
   * Cat de lat e un card, cand sunt putine zone alese.
   *
   * Ecranul vechi imparte cele 12 coloane Bootstrap la cate zone sunt bifate: o
   * singura zona lua toata latimea, doua cate jumatate. Grila tablourilor are
   * patru coloane, deci acelasi gand da: la una sau doua zone cardul ia doua
   * celule (`odb-x2`, cat ii da voie desenul), iar de la trei in sus cate una.
   * Numai peste 1100px - sub atat grila are oricum doua coloane, si sub 700 una
   * singura.
   */
  const wide = shown.length > 0 && shown.length <= 2

  return (
    <Content>
      <BoardLook>
        {query.isPending ? null : Object.keys(elements).length === 0 ? (
          /* Acelasi text si aceeasi iconita ca alerta veche `alert-info`, in
             cutia pe care zona asta o are pentru "nu e nimic de aratat". */
          <div className="odb-empty">
            <i className="fas fa-info-circle" aria-hidden="true" /> There are no dashboard elements selected!
          </div>
        ) : (
          <>
            <BoardStrip>
              {shown.map((group) => (
                <AreaStat key={group.key} icon={group.icon} title={group.title} value={counters[group.counter] ?? 0} />
              ))}
            </BoardStrip>

            <BoardGrid>
              {shown.map((group) =>
                group.apps ? (
                  <ApplicationsCard
                    key={group.key}
                    title={group.title}
                    icon={group.icon}
                    wide={wide}
                    apps={applications}
                    open={openApp}
                    onToggle={(id) => setOpenApp((cur) => (cur === id ? null : id))}
                  />
                ) : (
                  <DevicesCard
                    key={group.key}
                    title={group.title}
                    icon={group.icon}
                    wide={wide}
                    devices={groups[group.key] ?? []}
                    open={openDevices}
                    onToggle={(id) =>
                      setOpenDevices((cur) => (cur.includes(id) ? cur.filter((n) => n !== id) : [...cur, id]))
                    }
                  />
                )
              )}
            </BoardGrid>
          </>
        )}
      </BoardLook>
    </Content>
  )
}

/**
 * Contorul unei zone: aceleasi trei lucruri ca dala veche `dashboard-stat` -
 * cifra (sau "100%" cand nu e nimic cazut), UP / DOWN, si eticheta din josul
 * dalei, care acum e titlul.
 *
 * Regula "cate sunt cazute" e neschimbata: peste zero inseamna DOWN si rosu,
 * zero inseamna UP si verde, si atunci se scrie "100%" in loc de "0".
 */
function AreaStat({ icon, value, title }: { icon: string; value: number; title: string }) {
  const down = value > 0
  return (
    <BoardStat
      module="proactivitee"
      tone={down ? 'crit' : 'ok'}
      icon={icon}
      value={down ? value : 100}
      unit={down ? undefined : '%'}
      kicker={down ? 'Down' : 'Up'}
      title={title}
    />
  )
}

/**
 * Coloana aplicatiilor, devenita card.
 *
 * Ordinea randurilor e cea din portal - intai rosii, apoi galbenii, apoi verzii
 * - si randul spune ce spunea panoul: numele aplicatiei, cate masini are, si
 * starea, care era chenarul colorat si acum e bulina.
 */
function ApplicationsCard({
  title,
  icon,
  wide,
  apps,
  open,
  onToggle,
}: {
  title: string
  icon: string
  wide: boolean
  apps: App[]
  open: number | null
  onToggle: (id: number) => void
}) {
  const rows = orderByPanel(apps)
  return (
    <GroupCard title={title} icon={icon} wide={wide} rows={rows}>
      {rows.map((app) => (
        <BoardLine
          key={app.app_id}
          id={`app_${app.app_id}`}
          tone={toneOfPanel(app.panel)}
          label={app.app_name}
          value={`${app.devices} devices`}
          open={open === app.app_id}
          onToggle={() => onToggle(app.app_id)}
        >
          <ApplicationDown id={app.app_id} />
        </BoardLine>
      ))}
    </GroupCard>
  )
}

/** O categorie de masini, sau dezvoltarea: acelasi card, randuri fara cifra. */
function DevicesCard({
  title,
  icon,
  wide,
  devices,
  open,
  onToggle,
}: {
  title: string
  icon: string
  wide: boolean
  devices: Device[]
  open: number[]
  onToggle: (id: number) => void
}) {
  const rows = orderByPanel(devices)
  return (
    /* Discul langa cifra, ca pe cardul de aplicatie al vecinului: fara el, "28"
       colorat in rosu se citeste drept "28 cazute", cand inseamna "28 de masini,
       si printre ele e una in pericol". */
    <GroupCard title={title} icon={icon} wide={wide} rows={rows} unit={<i className="far fa-hdd" aria-hidden="true" />}>
      {rows.map((device) => (
        <BoardLine
          key={device.device_id}
          id={`device_${device.device_id}`}
          tone={toneOfPanel(device.panel)}
          label={device.device_name}
          open={open.includes(device.device_id)}
          onToggle={() => onToggle(device.device_id)}
        >
          <DeviceDown id={device.device_id} />
        </BoardLine>
      ))}
    </GroupCard>
  )
}

/**
 * Cutia comuna a celor sase: cifra e cate randuri are zona, tonul e cel mai rau
 * dintre ele, iar subsolul poarta aceleasi doua degete ca vecinul - cate merg
 * si cate nu.
 *
 * Lista se deruleaza in card (`BoardList`): celula are inaltime fixa, iar o
 * zona cu treizeci de masini n-are voie sa arate cinci si sa le ascunda pe
 * restul. Cardul tine doua randuri de grila (`tall`), ca lista sa aiba pe ce
 * incapea inainte sa fie nevoie de derulare.
 */
function GroupCard({
  title,
  icon,
  wide,
  rows,
  unit,
  children,
}: {
  title: string
  icon: string
  wide: boolean
  rows: readonly { panel: string }[]
  /** Ce sta lipit de cifra si spune ce anume se numara: discul, la masini. */
  unit?: ReactNode
  children: ReactNode
}) {
  const unwell = rows.filter((row) => row.panel !== 'panel-green').length
  const well = rows.length - unwell
  return (
    <BoardCard
      module="proactivitee"
      tone={worstTone(rows.map((row) => row.panel))}
      icon={icon}
      value={rows.length}
      unit={unit}
      kicker="Proactivitee"
      title={title}
      wide={wide}
      tall
      foot={
        <>
          <span className="odb-of">
            {well} <i className="far fa-thumbs-up" aria-hidden="true" />
          </span>
          <span className="odb-of">
            {unwell} <i className="far fa-thumbs-down" aria-hidden="true" />
          </span>
        </>
      }
    >
      <BoardList>{rows.length === 0 ? <p className="odb-note">Nothing is monitored here yet.</p> : children}</BoardList>
    </BoardCard>
  )
}

/** Intai rosii, apoi galbenii, apoi verzii - ordinea din `applications.php`. */
function orderByPanel<T extends { panel: string }>(rows: T[]): T[] {
  const rank = (p: string) => (p === 'panel-red' ? 0 : p === 'panel-yellow' ? 1 : 2)
  return [...rows].sort((a, b) => rank(a.panel) - rank(b.panel))
}

/** applicationDetails.php: persoana de contact, apoi ce e cazut. */
function ApplicationDown({ id }: { id: number }) {
  const query = useQuery(applicationDownQuery(id))
  if (query.isPending) return <Waiting />
  const contact = query.data?.contact ?? null
  const entries = query.data?.entries ?? []
  return (
    <>
      {contact?.contact_name ? (
        <p className="odb-note">
          <i className="fa fa-user" aria-hidden="true" /> {contact.contact_name}
        </p>
      ) : null}
      {contact?.contact_email ? (
        <p className="odb-note">
          <i className="fa fa-envelope" aria-hidden="true" /> {contact.contact_email}
        </p>
      ) : null}
      {contact?.contact_phone ? (
        <p className="odb-note">
          <i className="fa fa-phone" aria-hidden="true" /> {contact.contact_phone}
        </p>
      ) : null}
      {entries.length === 0 ? (
        <p className="odb-note">No devices down</p>
      ) : (
        entries.map((entry, i) => <DownLine key={i} entry={entry} withDevice />)
      )}
    </>
  )
}

/** deviceDetails.php: serviciile cazute ale unei masini. */
function DeviceDown({ id }: { id: number }) {
  const query = useQuery(deviceDownQuery(id))
  if (query.isPending) return <Waiting />
  const entries = query.data?.entries ?? []
  if (entries.length === 0) return <p className="odb-note">No services down</p>
  return (
    <>
      {entries.map((entry, i) => (
        <DownLine key={i} entry={entry} withDevice={false} />
      ))}
    </>
  )
}

function Waiting() {
  return (
    <p className="odb-note">
      <i className="fas fa-circle-notch fa-spin" aria-hidden="true" /> Loading
    </p>
  )
}

/**
 * O cadere, in sertar: numele, cu bulina rosie sau galbena dupa gravitate, si
 * descrierile de dedesubt. Culoarea spunea acelasi lucru si inainte
 * (`color-red` / `color-yellow`), doar ca pe text; pe bulina se citeste la fel
 * si nu mai coloreaza cuvinte - iar numele intreg ramane in `title`, fiindca
 * randul il taie cu trei puncte cand cardul e ingust.
 */
function DownLine({ entry, withDevice }: { entry: DownEntry; withDevice: boolean }) {
  const name = withDevice && entry.device ? `${entry.device} - ${entry.service}` : entry.service
  return (
    <>
      <span className="odb-row">
        <span className={`odb-dot odb-c-${entry.type === 'critical' ? 'crit' : 'warn'}`} aria-hidden="true" />
        <span className="odb-row-label" title={name}>
          {name}
        </span>
      </span>
      {entry.details.map((text, i) => (
        <p className="odb-note" key={i}>
          -{text}
        </p>
      ))}
    </>
  )
}
